import { useEffect, useRef } from "react";
import { SESSION_INACTIVITY_LIMIT_MS, endSession, isSessionValid } from "../service/session";

// User interactions that count as activity and reset the idle timer. Passive listeners so scroll
// performance is unaffected.
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

/**
 * Redirects to the login page after SESSION_INACTIVITY_LIMIT_MS with no user interaction, and
 * immediately whenever the tab becomes visible again with an already-expired session token (the
 * common "left the tab open past the ~1h token lifetime" case). Mount once in the authenticated
 * shell (AdminPage) so the whole admin area is covered.
 */
export function useInactivityLogout(): void {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(endSession, SESSION_INACTIVITY_LIMIT_MS);
    };
    const endIfExpired = () => {
      if (!isSessionValid()) endSession();
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));
    document.addEventListener("visibilitychange", endIfExpired);
    resetTimer();
    endIfExpired();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
      document.removeEventListener("visibilitychange", endIfExpired);
    };
  }, []);
}
